import type { GameState } from './types';
import { RESOURCE_IDS } from './content';
import { SAVE_VERSION, createInitialState } from './state';

export const SAVE_KEY = 'emberline.save.v1';

/**
 * Minimal storage contract. The browser build passes localStorage; a future
 * mobile wrap can pass anything with the same three methods.
 */
export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface SaveEnvelope {
  version: number;
  savedAt: number;
  state: GameState;
}

/** Serialise to a JSON envelope. */
export function serialize(state: GameState): string {
  const envelope: SaveEnvelope = {
    version: SAVE_VERSION,
    savedAt: Date.now(),
    state,
  };
  return JSON.stringify(envelope);
}

/**
 * Parse a save envelope, run the migration chain, and repair any fields that
 * a hand-edited or older save might be missing.
 */
export function deserialize(raw: string): GameState | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (!parsed || typeof parsed !== 'object') return null;
  const envelope = parsed as Partial<SaveEnvelope>;
  if (!envelope.state || typeof envelope.state !== 'object') return null;

  const migrated = migrate(envelope.state as GameState, envelope.version ?? 0);
  return repair(migrated);
}

/**
 * Version migration chain. Each step upgrades one version to the next, so a
 * save from any released version can be brought forward.
 */
function migrate(state: GameState, fromVersion: number): GameState {
  let working = state;
  let version = fromVersion;

  // No released versions before 1 yet; the chain is here so that adding one
  // later is a matter of appending a case rather than reshaping this function.
  while (version < SAVE_VERSION) {
    switch (version) {
      default:
        version = SAVE_VERSION;
        break;
    }
  }

  working.version = SAVE_VERSION;
  return working;
}

/** Fill in anything missing so a partial save cannot crash the simulation. */
function repair(state: GameState): GameState {
  const fresh = createInitialState(state.createdAt ?? Date.now());

  const repaired: GameState = {
    ...fresh,
    ...state,
    resources: { ...fresh.resources, ...(state.resources ?? {}) },
    jobs: { ...(state.jobs ?? {}) },
    buildings: { ...(state.buildings ?? {}) },
    techs: Array.isArray(state.techs) ? [...state.techs] : [],
    ancestral: { ...(state.ancestral ?? {}) },
    buffs: Array.isArray(state.buffs) ? [...state.buffs] : [],
    tales: Array.isArray(state.tales) ? [...state.tales] : [],
    stats: {
      ...fresh.stats,
      ...(state.stats ?? {}),
      lifetimeProduced: {
        ...fresh.stats.lifetimeProduced,
        ...(state.stats?.lifetimeProduced ?? {}),
      },
    },
    settings: { ...fresh.settings, ...(state.settings ?? {}) },
  };

  // Numeric guards — a corrupted number anywhere would poison every rate.
  for (const resourceId of RESOURCE_IDS) {
    const amount = repaired.resources[resourceId];
    if (!Number.isFinite(amount) || amount < 0) repaired.resources[resourceId] = 0;
  }
  if (!Number.isFinite(repaired.population) || repaired.population < 1) repaired.population = 1;
  if (!Number.isFinite(repaired.peakPopulation)) repaired.peakPopulation = repaired.population;
  if (!Number.isFinite(repaired.ancestralKnowledge) || repaired.ancestralKnowledge < 0) {
    repaired.ancestralKnowledge = 0;
  }
  if (!Number.isFinite(repaired.lastTick)) repaired.lastTick = Date.now();
  if (!Number.isFinite(repaired.seed)) repaired.seed = (Date.now() ^ 0x9e3779b9) >>> 0;

  return repaired;
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

export function saveToStorage(storage: StorageAdapter, state: GameState): void {
  try {
    storage.setItem(SAVE_KEY, serialize(state));
  } catch {
    // Storage full or blocked (private browsing). The game keeps running.
  }
}

export function loadFromStorage(storage: StorageAdapter): GameState | null {
  try {
    const raw = storage.getItem(SAVE_KEY);
    return raw ? deserialize(raw) : null;
  } catch {
    return null;
  }
}

export function clearStorage(storage: StorageAdapter): void {
  try {
    storage.removeItem(SAVE_KEY);
  } catch {
    // Nothing to do.
  }
}

// ---------------------------------------------------------------------------
// Export / import strings
// ---------------------------------------------------------------------------

const EXPORT_PREFIX = 'EMBERLINE1:';

function toBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(encoded: string): string {
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new TextDecoder().decode(bytes);
}

/** A shareable, paste-friendly save string. */
export function exportSave(state: GameState): string {
  return EXPORT_PREFIX + toBase64(serialize(state));
}

/** Accepts an exported string, or raw JSON pasted directly. */
export function importSave(text: string): GameState | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith(EXPORT_PREFIX)) {
    try {
      return deserialize(fromBase64(trimmed.slice(EXPORT_PREFIX.length)));
    } catch {
      return null;
    }
  }
  return deserialize(trimmed);
}
